/**
 * Date utilities for consistent cross-browser date handling
 * Addresses timezone, parsing, and mobile browser compatibility issues
 */

import { toBusinessDate } from '../date/businessDate';
import { LOCALE_DATE_FORMAT } from './constants';

export interface NormalizedDate {
    date: Date;
    hours: number;
    minutes: number;
    day: number; // 0 = Sunday, 1 = Monday, etc.
    dayName: string;
    isoString: string;
    localDateString: string;
}

export const DAYS_OF_WEEK = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
] as const;

/**
 * Safely create a Date object with consistent behavior across browsers
 */
export const createSafeDate = (input: Date | string | number): Date => {
    let date: Date;

    if (input instanceof Date) {
        date = new Date(input.getTime()); // Clone to avoid mutations
    } else if (typeof input === 'string') {
        // Handle ISO strings and other formats consistently
        if (input.includes('T')) {
            date = new Date(input);
        } else {
            // For date-only strings, ensure consistent parsing
            date = new Date(input + 'T00:00:00');
        }
    } else {
        date = new Date(input);
    }

    // Validate the date
    if (isNaN(date.getTime())) {
        throw new Error(`Invalid date: ${input}`);
    }

    return date;
};

/**
 * Normalize a date for consistent processing across browsers
 */
export const normalizeDate = (
    input: Date | string | number
): NormalizedDate => {
    const date = createSafeDate(input);

    return {
        date,
        hours: date.getHours(),
        minutes: date.getMinutes(),
        day: date.getDay(),
        dayName: DAYS_OF_WEEK[date.getDay()],
        isoString: toBusinessDate(date),
        localDateString: date.toLocaleDateString(LOCALE_DATE_FORMAT), // Consistent UK format
    };
};

/**
 * Calculate days between two dates with consistent rounding
 */
export const daysBetween = (
    startDate: Date | string,
    endDate: Date | string
): number => {
    const start = createSafeDate(startDate);
    const end = createSafeDate(endDate);

    // Use UTC to avoid timezone issues
    const startUTC = Date.UTC(
        start.getFullYear(),
        start.getMonth(),
        start.getDate()
    );
    const endUTC = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());

    const millisecondsPerDay = 24 * 60 * 60 * 1000;
    return Math.ceil((endUTC - startUTC) / millisecondsPerDay);
};

/**
 * Add days to a date safely
 */
export const addDays = (date: Date | string, days: number): Date => {
    const baseDate = createSafeDate(date);
    const result = new Date(baseDate.getTime());
    result.setDate(result.getDate() + days);
    return result;
};

/**
 * Set time on a date safely
 */
export const setTime = (
    date: Date | string,
    hours: number,
    minutes: number = 0
): Date => {
    const baseDate = createSafeDate(date);
    const result = new Date(baseDate.getTime());
    result.setHours(hours, minutes, 0, 0);
    return result;
};

/**
 * Format date consistently for comparison with bank holidays/closed dates
 * ALWAYS use en-GB locale regardless of browser settings
 */
export const formatForComparison = (date: Date | string): string => {
    const normalized = normalizeDate(date);
    // Force en-GB locale to match client expectations
    return normalized.date.toLocaleDateString(LOCALE_DATE_FORMAT);
};

/**
 * Convert time to minutes for easy comparison
 */
export const timeToMinutes = (hours: number, minutes: number): number => {
    return hours * 60 + minutes;
};

/**
 * Check if a time is after another time
 */
export const isTimeAfter = (
    hours1: number,
    minutes1: number,
    hours2: number,
    minutes2: number
): boolean => {
    return timeToMinutes(hours1, minutes1) > timeToMinutes(hours2, minutes2);
};

/**
 * Debug helper - logs date info for troubleshooting browser differences
 */
export const debugDate = (label: string, date: Date | string): void => {
    const normalized = normalizeDate(date);
    console.log(`[${label}]`, {
        original: date,
        normalized: normalized.date,
        isoString: normalized.isoString,
        localString: normalized.date.toLocaleDateString(LOCALE_DATE_FORMAT), // Force en-GB
        dayName: normalized.dayName,
        hours: normalized.hours,
        minutes: normalized.minutes,
        timezone: Intl.DateTimeFormat().resolvedOptions().locale, // Show detected locale
        userAgent: navigator.userAgent.substring(0, 50) + '...',
    });
};
